import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import {
  AbstractListenerService,
  type HubListenerCallback,
} from './AbstractListenerService';
import { type ILoggerService } from './LoggerService';

type HubServiceOptions = {
  path: string;
  logger: ILoggerService;
};

export interface IHubService {
  start(): Promise<void>;
  stop(): Promise<void>;
  send(methodName: string, ...args: any[]): Promise<void>;
  on(eventName: string, callback: HubListenerCallback): string;
  off(listenerIds: string[]): void;
}

export enum HubServiceEvents {
  ReceiveStartedAsync = 'ReceiveStartedAsync',
  ReceiveCompletedAsync = 'ReceiveCompletedAsync',
  ReceiveErrorAsync = 'ReceiveErrorAsync',
}

export class HubService extends AbstractListenerService implements IHubService {
  private connection: HubConnection;

  constructor({ path, logger }: HubServiceOptions) {
    super(logger);
    this.connection = new HubConnectionBuilder()
      .withUrl(path)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();
    this.logger.trace('Connection created');

    this.on(HubServiceEvents.ReceiveStartedAsync, (data) => {
      this.logger.trace(
        `Started with id ${data.id} and debugUrl ${data.debugUrl}`,
      );
    });

    this.on(HubServiceEvents.ReceiveCompletedAsync, (url: string) => {
      this.logger.trace(`Stopped with url ${url}`);
    });

    this.on(HubServiceEvents.ReceiveErrorAsync, (message: string) => {
      this.logger.trace(`Error received from hub: ${message}`);
    });
  }

  protected listenerAdded(
    eventName: string,
    callback: HubListenerCallback,
  ): void {
    this.connection.on(eventName, callback);
  }

  protected listenerRemoved(
    eventName: string,
    callback: HubListenerCallback,
  ): void {
    this.connection.off(eventName, callback);
  }

  public async start() {
    if (this.connection.state === 'Disconnected') {
      this.logger.trace('Starting connection');
      return this.connection.start();
    }
  }

  public async stop() {
    if (this.connection.state === 'Connected') {
      this.logger.trace('Connection stopped');
      return this.connection.stop();
    }
  }

  public async send(methodName: string, ...args: unknown[]) {
    if (this.connection.state === 'Connected') {
      this.logger.trace(`Sending message ${methodName}`, { data: { ...args } });
      return this.connection.send(methodName, ...args);
    }
  }
}
